/**
 * Whey Refund - Frontend JS (INP Optimized)
 * Backend: Google Apps Script web app
 * Flow: Click "Request Refund" -> Form (reasons, rating, feedback) -> Submit -> Show status
 *
 * INP Strategy:
 * - Event delegation (single click listener on document)
 * - requestAnimationFrame for all DOM mutations
 * - scheduler.yield() to break long tasks
 * - Lazy modal DOM caching (only when first opened)
 * - requestIdleCallback for non-critical checks
 */
(function () {
  'use strict';

  var module = document.getElementById('whey-refund-module');
  if (!module) return;

  /* --- Config --- */
  var BACKEND_URL = module.dataset.backendUrl || '';
  var VARIANT_ID = module.dataset.variantId || '';

  /* --- State --- */
  var _submitting = false;
  var _rating = 0;
  var _modalCached = false;
  var els = {};

  /* --- Yield to main thread (INP helper) --- */
  function yieldToMain() {
    if (typeof scheduler !== 'undefined' && scheduler.yield) return scheduler.yield();
    return new Promise(function (r) { setTimeout(r, 0); });
  }

  /* --- RAF-batched DOM write --- */
  function domWrite(fn) { requestAnimationFrame(fn); }

  /* --- Customer ID (memoized, dynamic) --- */
  var _customerId = null;
  function getCustomerId() {
    if (_customerId) return _customerId;
    try {
      var kpId = localStorage.getItem('kp_customer_id');
      if (kpId && kpId.trim()) { _customerId = kpId.trim(); return _customerId; }
    } catch (e) {}
    var dataId = module.dataset.customerId;
    if (dataId && dataId.trim()) { _customerId = dataId.trim(); return _customerId; }
    if (window.GNCFlitsConfig && window.GNCFlitsConfig.customerId) {
      _customerId = String(window.GNCFlitsConfig.customerId); return _customerId;
    }
    if (window.customer && window.customer.id) {
      _customerId = String(window.customer.id); return _customerId;
    }
    return null;
  }

  function isLoggedIn() {
    return module.dataset.loggedIn === 'true' || !!getCustomerId();
  }

  /* --- Backend fetch --- */
  function postToBackend(payload) {
    if (!BACKEND_URL) return Promise.reject(new Error('No backend URL'));
    return fetch(BACKEND_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(payload),
      keepalive: true
    }).then(function (r) { return r.json(); });
  }

  /* --- Modal DOM caching (lazy) --- */
  function ensureModalCached() {
    if (_modalCached) return true;
    var modal = document.getElementById('wr-modal');
    if (!modal) return false;
    els.modal = modal;
    els.overlay = document.getElementById('wr-modal-overlay');
    els.closeBtn = document.getElementById('wr-modal-close');
    els.cancelBtn = document.getElementById('wr-cancel-btn');
    els.submitBtn = document.getElementById('wr-submit-btn');
    els.reasons = document.getElementById('wr-reasons');
    els.stars = document.getElementById('wr-stars');
    els.feedback = document.getElementById('wr-feedback');
    els.msg = document.getElementById('wr-modal-msg');
    _modalCached = true;
    return true;
  }

  /* --- Status message helper --- */
  function showMsg(msg, type) {
    if (!els.msg) return;
    domWrite(function () {
      els.msg.textContent = msg;
      els.msg.className = 'wr-msg wr-msg--' + type;
    });
  }
  function hideMsg() {
    if (!els.msg) return;
    domWrite(function () { els.msg.className = 'wr-msg'; els.msg.textContent = ''; });
  }

  /* --- Modal open/close --- */
  function openModal() {
    if (!ensureModalCached()) return;
    domWrite(function () {
      els.modal.classList.add('wr-modal--open');
      document.body.style.overflow = 'hidden';
      hideMsg();
    });
  }
  function closeModal() {
    if (!els.modal) return;
    domWrite(function () {
      els.modal.classList.remove('wr-modal--open');
      document.body.style.overflow = '';
    });
  }

  /* --- Star rating interaction --- */
  function setRating(value) {
    _rating = value;
    renderStars(value);
  }

  function renderStars(fillCount) {
    if (!els.stars) return;
    var stars = els.stars.querySelectorAll('.wr-star');
    domWrite(function () {
      for (var i = 0; i < stars.length; i++) {
        if (i < fillCount) {
          stars[i].classList.add('wr-star--active');
        } else {
          stars[i].classList.remove('wr-star--active');
        }
      }
    });
  }

  function handleStarHover(e) {
    var star = e.target.closest('.wr-star');
    if (star) {
      renderStars(parseInt(star.dataset.value, 10));
    }
  }

  function handleStarLeave() {
    renderStars(_rating);
  }

  /* --- Checkbox tag toggle --- */
  function toggleTag(label) {
    var input = label.querySelector('input');
    if (!input) return;
    input.checked = !input.checked;
    domWrite(function () {
      if (input.checked) {
        label.classList.add('wr-tag--checked');
      } else {
        label.classList.remove('wr-tag--checked');
      }
    });
  }

  /* --- Get selected reasons --- */
  function getSelectedReasons() {
    if (!els.reasons) return [];
    var checked = els.reasons.querySelectorAll('input:checked');
    var reasons = [];
    for (var i = 0; i < checked.length; i++) {
      reasons.push(checked[i].value);
    }
    return reasons;
  }

  /* --- Submit refund request --- */
  async function submitRefund() {
    if (_submitting) return;

    var reasons = getSelectedReasons();
    if (reasons.length === 0) {
      showMsg('Please select at least one reason', 'error');
      return;
    }

    var confirmCheck = document.getElementById('wr-confirm-check');
    if (!confirmCheck || !confirmCheck.checked) {
      showMsg('Please confirm the checkbox to proceed', 'error');
      return;
    }

    var feedback = els.feedback ? els.feedback.value.trim() : '';
    var customerId = getCustomerId();

    if (!customerId) {
      showMsg('Session expired. Please refresh and try again.', 'error');
      return;
    }

    _submitting = true;
    hideMsg();

    domWrite(function () {
      if (els.submitBtn) {
        els.submitBtn.dataset.origText = els.submitBtn.textContent;
        els.submitBtn.innerHTML = '<span class="wr-spinner"></span> Please wait...';
        els.submitBtn.disabled = true;
      }
    });

    await yieldToMain();

    try {
      var data = await postToBackend({
        action: 'claim-refund',
        customer_id: customerId,
        variant_id: VARIANT_ID,
        reason: reasons.join(', '),
        feedback: feedback,
        rating: _rating
      });

      if (data.success) {
        console.log('[GNC Whey Refund] claim-refund SUCCESS. Raw data.amount:', data.amount, '| Type:', typeof data.amount);
        showMsg('Refund requested successfully!', 'success');
        setTimeout(function () {
          closeModal();
          showRefundStatus('requested', data.amount || 0);
          // Notify buy-buttons script to update ATC buttons
          window.dispatchEvent(new CustomEvent('whey:refund-requested'));
        }, 1500);
      } else {
        showMsg(data.reason || 'Refund could not be processed. Please contact support.', 'error');
        _submitting = false;
        domWrite(function () {
          if (els.submitBtn) {
            els.submitBtn.textContent = els.submitBtn.dataset.origText || 'Request Refund';
            els.submitBtn.disabled = false;
          }
        });
      }
    } catch (e) {
      showMsg('Network error. Please try again.', 'error');
      _submitting = false;
      domWrite(function () {
        if (els.submitBtn) {
          els.submitBtn.textContent = els.submitBtn.dataset.origText || 'Request Refund';
          els.submitBtn.disabled = false;
        }
      });
    }
  }

  /* --- Show refund status card (replaces eligible state) --- */
  function showRefundStatus(status, amount) {
    var container = document.getElementById('wr-state-container');
    if (!container) return;

    var amt = parseFloat(amount) || 0;
    var isSuccess = (status === 'successful');

    console.log('[GNC Whey Refund] showRefundStatus called. status:', status, '| raw amount arg:', amount, '| parsed amt:', amt);

    // Persist the amount when refund is first requested so we can reuse
    // it on the "Successfully Refunded" state (check-eligibility amount is unreliable)
    if (!isSuccess && amt > 0) {
      try { localStorage.setItem('gnc_whey_refund_amt', amt); console.log('[GNC Whey Refund] Saved to localStorage:', amt); } catch(e) {}
    }
    if (isSuccess && amt === 0) {
      try {
        var stored = localStorage.getItem('gnc_whey_refund_amt');
        console.log('[GNC Whey Refund] isSuccess + amt=0, localStorage value:', stored);
        amt = parseFloat(stored) || 0;
      } catch(e) {}
    }

    console.log('[GNC Whey Refund] Final amt used in UI:', amt);

    var fillWidth = isSuccess ? '100%' : '50%';
    var title = isSuccess ? '₹' + amt + ' Successfully Refunded' : '₹' + amt + ' Refund Requested';
    var subtitle = isSuccess
      ? 'Credited To Your Original Payment Source'
      : 'Expected Within 3–5 Business Days';

    var dot1Class = isSuccess ? 'wr-steps__dot' : 'wr-steps__dot wr-steps__dot--current';
    var dot2Class = isSuccess ? 'wr-steps__dot wr-steps__dot--current' : 'wr-steps__dot';
    var label1Class = isSuccess ? 'done' : 'current';
    var label2Class = isSuccess ? 'current' : '';

    domWrite(function () {
      container.innerHTML =
        '<div class="wr-card">' +
          '<div class="wr-card__title"><h5><span class="wr-dot wr-dot--green"></span> ' + title + '</h5></div>' +
          '<p class="wr-card__subtitle">' + subtitle + '</p>' +
          '<div class="wr-steps">' +
            '<div class="wr-steps__track">' +
              '<span class="' + dot1Class + '"></span>' +
              '<span class="' + dot2Class + '"></span>' +
              '<div class="wr-steps__bar"><div class="wr-steps__fill" style="width:' + fillWidth + '"></div></div>' +
            '</div>' +
            '<div class="wr-steps__labels">' +
              '<span class="' + label1Class + '">Requested</span>' +
              '<span class="' + label2Class + '">Successful</span>' +
            '</div>' +
          '</div>' +
          '<p class="wr-coins">\uD83C\uDF81 You Keep Your 100 GNC Coins, Use On Your Next Order.</p>' +
        '</div>';
    });
  }

  /* --- Countdown timer --- */
  function startCountdown() {
    var el = document.getElementById('wr-countdown');
    var fill = document.getElementById('wr-progress-fill');
    if (!el) return;

    // Fetch delivery date from backend to calculate remaining time
    var customerId = getCustomerId();
    if (!customerId) return;

    postToBackend({
      action: 'check-eligibility',
      customer_id: customerId,
      variant_id: VARIANT_ID
    }).then(function (data) {
      if (data.status === 'eligible' && data.order_date) {
        var orderDate = new Date(data.order_date);
        var expiryDate = new Date(orderDate.getTime() + 3 * 24 * 60 * 60 * 1000);
        updateCountdown(el, fill, expiryDate, orderDate);
        setInterval(function () { updateCountdown(el, fill, expiryDate, orderDate); }, 60000);
      } else if (data.status === 'eligible') {
        // No order_date from backend, show generic
        el.textContent = '3d 0h Left';
      } else if (data.status === 'claimed') {
        showRefundStatus('successful', data.amount || 0);
      } else if (data.status === 'expired') {
        domWrite(function () {
          el.textContent = 'Expired';
          if (fill) fill.style.width = '0%';
          var btn = document.getElementById('wr-request-refund-btn');
          if (btn) { btn.disabled = true; btn.className = 'wr-btn wr-btn--disabled'; btn.textContent = 'Refund window expired'; }
        });
      }
    }).catch(function () {});
  }

  function updateCountdown(el, fill, expiryDate, orderDate) {
    var now = Date.now();
    var remaining = expiryDate.getTime() - now;
    var total = expiryDate.getTime() - orderDate.getTime();

    if (remaining <= 0) {
      domWrite(function () {
        el.textContent = 'Expired';
        if (fill) fill.style.width = '0%';
        var btn = document.getElementById('wr-request-refund-btn');
        if (btn) { btn.disabled = true; btn.className = 'wr-btn wr-btn--disabled'; btn.textContent = 'Refund window expired'; }
      });
      return;
    }

    var days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    var hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    var pct = Math.max(0, Math.min(100, (remaining / total) * 100));

    domWrite(function () {
      el.textContent = days + 'd ' + hours + 'h Left';
      if (fill) fill.style.width = pct + '%';
    });
  }

  /* --- Event Delegation --- */
  function handleClick(e) {
    var target = e.target;

    // Star rating
    var star = target.closest('.wr-star');
    if (star) {
      e.preventDefault();
      setRating(parseInt(star.dataset.value, 10));
      return;
    }

    // Checkbox tag
    var tag = target.closest('.wr-tag');
    if (tag) {
      e.preventDefault();
      toggleTag(tag);
      return;
    }

    // ID-based buttons
    var idTarget = target.closest('[id]');
    if (!idTarget) return;

    switch (idTarget.id) {
      case 'wr-request-refund-btn':
        e.preventDefault();
        openModal();
        break;
      case 'wr-modal-overlay':
      case 'wr-modal-close':
      case 'wr-cancel-btn':
        e.preventDefault();
        closeModal();
        break;
      case 'wr-submit-btn':
        e.preventDefault();
        submitRefund();
        break;
      case 'whey-refund-login-btn':
        e.preventDefault();
        if (typeof handleKpAndShopifyLogin === 'function') {
          handleKpAndShopifyLogin(window.location.pathname);
        }
        break;
    }
  }

  /* --- Escape key --- */
  function handleKeydown(e) {
    if (e.key === 'Escape' && els.modal && els.modal.classList.contains('wr-modal--open')) {
      closeModal();
    }
  }

  /* --- Eligibility check (deferred) --- */
  function checkEligibilityAfterLogin() {
    if (!isLoggedIn()) return;
    if (module.dataset.refundClaimed === 'true') return;

    var customerId = getCustomerId();
    if (!customerId) return;

    postToBackend({
      action: 'check-eligibility',
      customer_id: customerId,
      variant_id: VARIANT_ID
    }).then(function (data) {
      if (data.status === 'claimed') {
        showRefundStatus('successful', data.amount || 0);
      }
    }).catch(function () {});
  }

  /* --- Init --- */
  function init() {
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeydown);

    // Star hover behavior
    var starsContainer = document.getElementById('wr-stars');
    if (starsContainer) {
      starsContainer.addEventListener('mouseover', handleStarHover);
      starsContainer.addEventListener('mouseleave', handleStarLeave);
    }

    // Start countdown if eligible state is shown
    if (document.getElementById('wr-state-eligible')) {
      startCountdown();
    }

    // KwikPass post-login
    window.addEventListener('update-dom', function () {
      _customerId = null;
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(checkEligibilityAfterLogin);
      } else {
        setTimeout(checkEligibilityAfterLogin, 300);
      }
    });

    // Check if logged in via JS but Liquid didn't know
    if (module.dataset.loggedIn !== 'true' && isLoggedIn()) {
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(checkEligibilityAfterLogin);
      } else {
        setTimeout(checkEligibilityAfterLogin, 300);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
